/**
 * Labstep
 *
 * @module components/Metadata/Table
 * @desc Table listing all metadatas
 */

import { useHasAccess } from 'labstep-web/components/Entity/Can/hooks';
import { Action } from 'labstep-web/components/Entity/Can/types';
import { entityTableDragSortableBody } from 'labstep-web/components/Entity/Table/DragSortable';
import { MetadataActionCreateModalContainer as MetadataActionCreateModal } from 'labstep-web/components/Metadata/Action/Create/Modal';
import { MetadataActionMenu } from 'labstep-web/components/Metadata/Action/Menu';
import { MetadataDragIcon } from 'labstep-web/components/Metadata/DragIcon';
import { MetadataFormShowEditValueWithClear } from 'labstep-web/components/Metadata/Form/ShowEdit/Value/WithClear';
import { MetadataRequired } from 'labstep-web/components/Metadata/Required';
import { MetadataTableExtraRows } from 'labstep-web/components/Metadata/Table/ExtraRows';
import { Flex } from 'labstep-web/core/Flex';
import { Loadable } from 'labstep-web/core/Loadable';
import type { TableColumnType } from 'labstep-web/core/Table/Simple/types';
import { Metadata } from 'labstep-web/models/metadata';
import { MetadataType } from 'labstep-web/models/metadata/types';
import { Resource } from 'labstep-web/models/resource.model';
import { ResourceItem } from 'labstep-web/models/resource-item.model';
import React from 'react';
import styles from './styles.module.scss';
import type { MetadataTableProps } from './types';

export const MetadataTableMoleculeAdditionalRows = React.lazy(() =>
  import('./MoleculeAdditionalRows').then((module) => ({
    default: module.MetadataTableMoleculeAdditionalRowsContainer,
  })),
);

/**
 * function to select correct metadata object
 */
const selectMetadata = (
  replacementMetadatas: Metadata[],
  metadata: Metadata,
): Metadata => {
  return (
    replacementMetadatas.find(
      (met) => met.template_id === metadata.id,
    ) || metadata
  );
};

export const MetadataTable: React.FC<MetadataTableProps> = ({
  replacementMetadatas,
  entity,
  actions,
  isTemplate,
  disabled,
  extraRows = [],
  onCreateSuccess,
  baseMetadatas,
}) => {
  const canEdit = useHasAccess(
    entity.entityName,
    entity.idAttr,
    Action.edit,
  );
  let templateEntity = entity;
  if (entity instanceof Resource || entity instanceof ResourceItem) {
    if (entity.nonDeletedTemplate) {
      templateEntity = entity.nonDeletedTemplate;
    }
  }

  // Filter out metadata fields that have lookup_relation_field_definition
  // These will be shown as additional rows under their parent
  const filteredBaseMetadatas = baseMetadatas; /* .filter(
    (metadata) =>
      !metadata.field_definition?.lookup_relation_field_definition,
  ); */

  // Create a map of parent field_definition.id to lookup relation fields
  /*  const lookupFieldsByParent = React.useMemo(() => {
    const map = new Map<string | number, Metadata[]>();
    baseMetadatas.forEach((metadata) => {
      const lookupRelation =
        metadata.field_definition?.lookup_relation_field_definition;
      if (lookupRelation?.id) {
        const existing = map.get(lookupRelation.id) || [];
        map.set(lookupRelation.id, [...existing, metadata]);
      }
    });
    return map;
  }, [baseMetadatas]); */

  const columns: TableColumnType<Metadata>[] = [
    {
      header: 'Field',
      content: (metadata): React.ReactNode => {
        const parentEntity = metadata.is_template
          ? templateEntity
          : entity;
        return (
          <Flex className={styles.fieldName}>
            <div className={styles.insertBelow}>
              <MetadataActionCreateModal
                body={{ position_after_id: metadata.id }}
                entity={parentEntity}
                entityName={parentEntity.entityName}
                isTemplate={metadata.is_template}
                actionComponentProps={{
                  type: 'icon',
                  icon: 'plus',
                  elementProps: {
                    popup: { content: 'Insert Below' },
                  },
                }}
                options={{ onSuccess: onCreateSuccess }}
              />
            </div>
            <MetadataDragIcon metadata={metadata} />
            <MetadataRequired
              metadata={selectMetadata(
                replacementMetadatas,
                metadata,
              )}
            />
            <div className={styles.toggleHiddenVisible}>
              <MetadataActionMenu
                entity={entity}
                metadata={metadata}
                actions={actions}
                pointing={false}
              />
            </div>
          </Flex>
        );
      },
      cellProps: { width: 4 },
    },
    {
      header: isTemplate ? 'Default Value' : 'Value',
      content: (metadata): React.ReactNode => {
        const selectedMetadata = selectMetadata(
          replacementMetadatas,
          metadata,
        );

        return (
          <MetadataFormShowEditValueWithClear
            metadata={selectedMetadata}
            entity={entity}
          />
        );
      },
      cellProps: { style: { flex: 1 } },
    },
  ];

  return (
    <>
      {extraRows.length > 0 && (
        <MetadataTableExtraRows rows={extraRows} entity={entity} />
      )}
      {filteredBaseMetadatas.length > 0 && (
        <div id="metadata-table" data-cy="components/Metadata/Table">
          {entityTableDragSortableBody(
            Metadata.entityName,
            disabled || !canEdit,
            (value: Metadata) => {
              const selectedMetadata = selectMetadata(
                replacementMetadatas,
                value,
              );

              // Get lookup relation fields for this metadata
              /* const lookupFields = value.field_definition?.id
                ? lookupFieldsByParent.get(
                    value.field_definition.id,
                  ) || []
                : []; */

              return (
                <>
                  {value.type === MetadataType.molecule && (
                    <Loadable>
                      <MetadataTableMoleculeAdditionalRows
                        parent={selectedMetadata}
                        createProps={selectedMetadata.getCreateProps(
                          entity,
                        )}
                      />
                    </Loadable>
                  )}
                  {/* {lookupFields.length > 0 && (
                    <MetadataTableLookupRelationAdditionalRows
                      parent={selectedMetadata}
                      lookupFields={lookupFields}
                      entity={entity}
                      replacementMetadatas={replacementMetadatas}
                    />
                  )} */}
                </>
              );
            },
          )({ rows: filteredBaseMetadatas, columns })}
        </div>
      )}
    </>
  );
};
